import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { OAuthService } from 'angular-oauth2-oidc';
import { ProfileState, useBetterAuthForCurrentPlatform } from '@et/guards';
import { Store } from '@ngxs/store';
import { NgxPermissionsService } from 'ngx-permissions';
import {
  TimeoutError,
  catchError,
  combineLatest,
  filter,
  first,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
  timeout,
} from 'rxjs';

type ProfileResolveResult =
  | { resolved: true; userType: string }
  | { resolved: false };

@Injectable({
  providedIn: 'root',
})
export class ProfileResolver {
  private store = inject(Store);
  private msal = inject(MsalService, { optional: true });
  private oauth = inject(OAuthService, { optional: true });
  private router = inject(Router);
  private permissionsService = inject(NgxPermissionsService);

  resolve(): Observable<boolean> {
    return combineLatest([
      this.store.select(ProfileState.v2ProfileId),
      this.store.select(ProfileState.getUserType),
    ]).pipe(
      switchMap(([profileId, userType]) => {
        if (profileId && userType) {
          return of({ resolved: true, userType });
        }
        return this.handleMissingProfile();
      }),
      filter((result): result is ProfileResolveResult => result !== undefined),
      tap((result) => {
        if (result.resolved) {
          this.permissionsService.addPermission(result.userType);
        }
      }),
      map((result) => result.resolved),
      first(),
      timeout({ first: 15_000 }),
      catchError((err) => {
        if (err instanceof TimeoutError) {
          this.router.navigate(['/auth/login'], {
            queryParams: { error: 'session_timeout' },
          });
        }
        return of(false);
      }),
    );
  }

  /**
   * Profile data hasn't landed in the store yet. Two interpretations:
   *  - Session is still valid (token present / MSAL account present) → wait. `AppComponent`
   *    is responsible for dispatching `SetSelfProfile`, which will retrigger the upstream
   *    `combineLatest`. Returning `undefined` keeps the observable alive without resolving.
   *  - Session is gone → push the user back through the active auth flow's login route.
   */
  private handleMissingProfile() {
    if (useBetterAuthForCurrentPlatform()) {
      if (this.oauth?.hasValidAccessToken() || this.oauth?.hasValidIdToken()) {
        return of(undefined);
      }
      this.router.navigate(['/auth/login']);
      return of<ProfileResolveResult>({ resolved: false });
    }

    if (!this.msal) {
      this.router.navigate(['/auth/login']);
      return of<ProfileResolveResult>({ resolved: false });
    }
    const account = this.msal.instance.getActiveAccount();
    if (account) {
      return of(undefined);
    }
    return this.msal.logout().pipe(
      take(1),
      tap(() => this.router.navigate(['/'])),
      map(() => ({ resolved: false } as ProfileResolveResult)),
    );
  }
}
